import React from "react";

import { cn } from "../../utils/cn";

export interface ListRailProps {
    /** Top row — typically a `RailHeader` (count + action). */
    header?: React.ReactNode;
    /** Optional filter row below the header — e.g. a `ToggleMenu`. */
    filter?: React.ReactNode;
    /** The list body — rows (Domain Components) passed in. */
    children: React.ReactNode;
    /** Override the rail chrome (default `w-80`, right divider, scroll). */
    className?: string;
}

/**
 * ListRail — the vertical spine of a master–detail layout: an optional
 * header (count + action), an optional filter, and a scrollable list
 * body.
 *
 * Generic Component: every part is a slot. It imports no domain — the
 * rows (`ProgramRow`, `PolicyRow`, …) arrive through `children`, so the
 * same rail serves any domain. The header is usually a `RailHeader` and
 * the filter a `ToggleMenu`, but both are just `ReactNode`.
 *
 * @see ListRail.intent.md   policy + recipes
 * @see ListRail.layout.html intended layout
 */
export function ListRail({
    header,
    filter,
    children,
    className
}: ListRailProps): React.ReactElement {
    return (
        <aside
            className={cn(
                // Named container so header/filter slots can reflow on the
                // rail's OWN width (e.g. stack when it's narrowed), not the
                // viewport — inert unless a slot uses `@…/rail` variants.
                "@container/rail w-80 shrink-0 border-r border-line-base overflow-auto",
                className
            )}
        >
            {header}
            {filter ? <div className="px-4 pb-3">{filter}</div> : null}
            {children}
        </aside>
    );
}
